import { useEffect, useMemo, useState } from "react";

import { PalTextField } from "@palamar/fe-library";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import { Grid } from "@mui/material";

import useProjectChange from "~common/hooks/useProjectChange";
import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRPage from "~components/Generic/PRPage";
import HistoryHelper from "~helpers/HistoryHelper";
import {
  createOrUpdateInvoiceRecipient,
  getInvoiceRecipient,
  setInvoiceRecipient,
} from "~store/billingAndInvoicing/actions";
import { selectInvoiceRecipient } from "~store/billingAndInvoicing/selectors";
import { selectCurrentProject } from "~store/user/selectors";

const InvoiceRecipientsEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const invoiceRecipient = useSelector(selectInvoiceRecipient);
  const dispatch = useDispatch();
  const currentProject = useSelector(selectCurrentProject);

  useEffect(() => {
    if (!id) return;
    dispatch(getInvoiceRecipient(currentProject.id, id));

    return () => {
      dispatch(setInvoiceRecipient(null));
    };
  }, [dispatch, id, currentProject.id]);

  const [initialValues, setInitialValues] = useState({
    id: undefined,
    receiver_tax_code: "",
    receiver_name: "",
    receiver_address: "",
    receiver_city: "",
    receiver_town: "",
    receiver_postal_code: "",
  });

  useEffect(() => {
    setInitialValues({
      id: invoiceRecipient?.id || undefined,
      receiver_tax_code: invoiceRecipient?.receiver_tax_code || "",
      receiver_name: invoiceRecipient?.receiver_name || "",
      receiver_address: invoiceRecipient?.receiver_address || "",
      receiver_city: invoiceRecipient?.receiver_city || "",
      receiver_town: invoiceRecipient?.receiver_town || "",
      receiver_postal_code: invoiceRecipient?.receiver_postal_code || "",
    });
  }, [invoiceRecipient]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      receiver_tax_code: Yup.string().required(t("common.required")),
      receiver_name: Yup.string().required(t("common.required")),
      receiver_address: Yup.string().required(t("common.required")),
      receiver_city: Yup.string().required(t("common.required")),
      receiver_town: Yup.string().required(t("common.required")),
      receiver_postal_code: Yup.string().required(t("common.required")),
    }),
    onSubmit: async (values) => {
      await dispatch(createOrUpdateInvoiceRecipient(currentProject.id, values));
      handleClickCancel();
    },
  });

  useProjectChange(() => {
    HistoryHelper.push("/billing-and-invoicing/invoice-recipients", {
      scope: "dashboard",
    });
  }, []);

  const parentName = useMemo(
    () => [
      {
        label: t("layout.sidebar.invoiceRecipients"),
        url: "/billing-and-invoicing/invoice-recipients",
      },
      {
        label: id
          ? t("dashboard.invoiceRecipientsEdit.editRecipient")
          : t("dashboard.invoiceRecipientsEdit.createRecipient"),
      },
    ],
    [id, t]
  );

  const handleClickCancel = () => {
    HistoryHelper.goBack("/billing-and-invoicing/invoice-recipients/", { scope: "dashboard" });
  };

  return (
    <PRContainer name={t("layout.sidebar.billingAndInvoicing")} parentName={parentName}>
      <PRPage>
        <Grid
          container
          spacing={{
            xs: 1,
            md: 2,
          }}
        >
          <Grid item md={6} xs={12}>
            <PalTextField
              fullWidth
              invalid={formik.touched.receiver_tax_code && formik.errors.receiver_tax_code}
              label={t("dashboard.invoiceRecipients.receiverTaxCode")}
              name="receiver_tax_code"
              value={formik.values.receiver_tax_code}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>

          <Grid item md={6} xs={12}></Grid>
          <Grid item md={6} xs={12}>
            <PalTextField
              fullWidth
              invalid={formik.touched.receiver_name && formik.errors.receiver_name}
              label={t("dashboard.invoiceRecipients.receiverName")}
              name="receiver_name"
              value={formik.values.receiver_name}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6} xs={12}></Grid>
          <Grid item md={6} xs={12}>
            <PalTextField
              fullWidth
              multiline
              invalid={formik.touched.receiver_address && formik.errors.receiver_address}
              label={t("dashboard.invoiceRecipients.receiverAddress")}
              maxRows={4}
              minRows={2}
              name="receiver_address"
              value={formik.values.receiver_address}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6} xs={12}></Grid>
          <Grid item md={6} xs={12}>
            <PalTextField
              fullWidth
              invalid={formik.touched.receiver_city && formik.errors.receiver_city}
              label={t("dashboard.invoiceRecipients.receiverCity")}
              name="receiver_city"
              value={formik.values.receiver_city}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6} xs={12}></Grid>
          <Grid item md={6} xs={12}>
            <PalTextField
              fullWidth
              invalid={formik.touched.receiver_town && formik.errors.receiver_town}
              label={t("dashboard.invoiceRecipients.receiverTown")}
              name="receiver_town"
              value={formik.values.receiver_town}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6} xs={12}></Grid>
          <Grid item md={6} xs={12}>
            <PalTextField
              fullWidth
              invalid={formik.touched.receiver_postal_code && formik.errors.receiver_postal_code}
              label={t("dashboard.invoiceRecipients.receiverPostalCode")}
              name="receiver_postal_code"
              value={formik.values.receiver_postal_code}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6} xs={12}></Grid>
        </Grid>
        <Grid container mt={2}>
          <Grid item ml="auto">
            <PRButton outline className="me-2" onClick={handleClickCancel}>
              {t("common.cancel")}
            </PRButton>
            <PRButton onClick={formik.handleSubmit}>{id ? t("common.update") : t("common.create")}</PRButton>
          </Grid>
        </Grid>
      </PRPage>
    </PRContainer>
  );
};

export default InvoiceRecipientsEdit;
